import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseISO, format, isAfter, isBefore } from 'date-fns';
import { providersAPI } from '../../api/providers';
import { bookingsAPI } from '../../api/bookings';
import Avatar from '../../components/common/Avatar';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import { formatCurrency } from '../../utils/formatter';
import {
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  PhoneIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PER_PAGE = 10;

const PENDING_SET = new Set(['PENDING']);
const CONFIRMED_SET = new Set(['CONFIRMED', 'ACCEPTED', 'PAID']);
const COMPLETED_SET = new Set(['COMPLETED']);
const CANCELLED_SET = new Set(['CANCELLED', 'DECLINED', 'REFUNDED', 'NO_SHOW']);

const STATUS_META = {
  PENDING:   { label: 'Pending',   color: 'var(--orange-9)',     bg: 'var(--orange-3)' },
  CONFIRMED: { label: 'Confirmed', color: '#3b82f6',             bg: 'var(--blue-3, #dbeafe)' },
  ACCEPTED:  { label: 'Accepted',  color: '#3b82f6',             bg: 'var(--blue-3, #dbeafe)' },
  PAID:      { label: 'Paid',      color: '#3b82f6',             bg: 'var(--blue-3, #dbeafe)' },
  COMPLETED: { label: 'Completed', color: '#22c55e',             bg: 'var(--green-3, #dcfce7)' },
  CANCELLED: { label: 'Cancelled', color: 'var(--red-9, #e5484d)', bg: 'var(--red-3, #fee2e2)' },
  DECLINED:  { label: 'Declined',  color: 'var(--red-9, #e5484d)', bg: 'var(--red-3, #fee2e2)' },
  REFUNDED:  { label: 'Refunded',  color: '#a855f7',             bg: '#f3e8ff' },
  NO_SHOW:   { label: 'No-show',   color: 'var(--gray-11)',      bg: 'var(--gray-4)' },
};

const earningsAmount = (b) => {
  const subtotal = Number(b.subtotal || b.totalPrice || b.price || 0);
  const commission = Number(b.commission || 0);
  return Math.max(0, subtotal - commission);
};

const bookingDate = (b) => {
  const raw = b.startAt || b.scheduledAt || b.date || b.createdAt;
  if (!raw) return null;
  try { return typeof raw === 'string' ? parseISO(raw) : new Date(raw); }
  catch { return null; }
};

const ProviderBookings = () => {
  const [provider, setProvider] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const profile = await providersAPI.getMyProfile();
        if (cancelled) return;
        setProvider(profile);
        const data = await bookingsAPI.getBookings({ providerId: profile.id }, { silent: true });
        if (cancelled) return;
        setBookings(Array.isArray(data) ? data : data?.bookings || []);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        toast.error('Failed to load bookings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { setPage(1); }, [search, range, sort, activeTab]);

  const refetch = async () => {
    if (!provider?.id) return;
    try {
      const data = await bookingsAPI.getBookings({ providerId: provider.id }, { silent: true });
      setBookings(Array.isArray(data) ? data : data?.bookings || []);
    } catch { /* ignore */ }
  };

  const handleAction = async (booking, action) => {
    setActingId(booking.id);
    try {
      const pid = provider?.id;
      if (action === 'confirm') {
        await bookingsAPI.confirmBooking(booking.id, pid);
        toast.success('Booking accepted');
      } else if (action === 'decline') {
        const reason = window.prompt('Reason for declining (optional):');
        if (reason === null) { setActingId(null); return; }
        await bookingsAPI.declineBooking(booking.id, reason, pid);
        toast.success('Booking declined');
      } else if (action === 'complete') {
        await bookingsAPI.completeBooking(booking.id, pid);
        toast.success('Booking marked as complete');
      } else if (action === 'cancel') {
        const reason = window.prompt('Reason for cancelling (optional):');
        if (reason === null) { setActingId(null); return; }
        await bookingsAPI.cancelBooking(booking.id, { reason });
        toast.success('Booking cancelled');
      } else if (action === 'no-show') {
        if (!window.confirm('Mark this booking as a no-show?')) { setActingId(null); return; }
        await bookingsAPI.markNoShow(booking.id);
        toast.success('Marked as no-show');
      }
      await refetch();
    } catch (error) {
      console.error('Booking action error:', error);
      toast.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { all: bookings.length, pending: 0, confirmed: 0, completed: 0, cancelled: 0 };
    bookings.forEach((b) => {
      if (PENDING_SET.has(b.status)) c.pending++;
      else if (CONFIRMED_SET.has(b.status)) c.confirmed++;
      else if (COMPLETED_SET.has(b.status)) c.completed++;
      else if (CANCELLED_SET.has(b.status)) c.cancelled++;
    });
    return c;
  }, [bookings]);

  const tabMatches = (status) => {
    switch (activeTab) {
      case 'pending':   return PENDING_SET.has(status);
      case 'confirmed': return CONFIRMED_SET.has(status);
      case 'completed': return COMPLETED_SET.has(status);
      case 'cancelled': return CANCELLED_SET.has(status);
      default: return true;
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const rangeMs = {
      all: Infinity,
      today: 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
    }[range] ?? Infinity;

    let list = bookings.filter((b) => {
      if (!tabMatches(b.status)) return false;
      if (rangeMs !== Infinity) {
        const d = bookingDate(b);
        if (!d) return false;
        if (Math.abs(now - d.getTime()) > rangeMs) return false;
      }
      if (q) {
        const client = (b.client?.name || b.clientName || '').toLowerCase();
        const phone = (b.client?.phone || '').toLowerCase();
        const email = (b.client?.email || '').toLowerCase();
        const service = (b.service?.name || b.serviceName || '').toLowerCase();
        const id = (b.id || '').toString().toLowerCase();
        if (!client.includes(q) && !phone.includes(q) && !email.includes(q) && !service.includes(q) && !id.includes(q)) {
          return false;
        }
      }
      return true;
    });

    list.sort((a, b) => {
      const aDate = bookingDate(a)?.getTime() || 0;
      const bDate = bookingDate(b)?.getTime() || 0;
      const aAmt = earningsAmount(a);
      const bAmt = earningsAmount(b);
      switch (sort) {
        case 'oldest':  return aDate - bDate;
        case 'highest': return bAmt - aAmt;
        case 'lowest':  return aAmt - bAmt;
        case 'upcoming': {
          // Upcoming first (future dates ascending), then past
          const now2 = Date.now();
          const aFuture = aDate >= now2;
          const bFuture = bDate >= now2;
          if (aFuture && !bFuture) return -1;
          if (!aFuture && bFuture) return 1;
          return aFuture ? aDate - bDate : bDate - aDate;
        }
        default: return bDate - aDate;
      }
    });

    return list;
  }, [bookings, activeTab, search, range, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const filteredEarnings = useMemo(
    () => filtered
      .filter((b) => COMPLETED_SET.has(b.status))
      .reduce((sum, b) => sum + earningsAmount(b), 0),
    [filtered]
  );

  const hasActiveFilters = search || range !== 'all' || sort !== 'newest';

  const clearFilters = () => {
    setSearch('');
    setRange('all');
    setSort('newest');
  };

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const rows = [
      ['ID', 'Date', 'Client', 'Phone', 'Service', 'Status', 'Earnings'],
      ...filtered.map((b) => [
        b.id || '',
        bookingDate(b)?.toISOString() || '',
        (b.client?.name || b.clientName || '').replace(/,/g, ' '),
        b.client?.phone || '',
        (b.service?.name || b.serviceName || '').replace(/,/g, ' '),
        b.status || '',
        earningsAmount(b),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Bookings exported');
  };

  const tabs = [
    { id: 'all',       label: 'All',       icon: ListBulletIcon,    count: counts.all },
    { id: 'pending',   label: 'Pending',   icon: ClockIcon,         count: counts.pending },
    { id: 'confirmed', label: 'Upcoming',  icon: CalendarDaysIcon,  count: counts.confirmed },
    { id: 'completed', label: 'Completed', icon: CheckCircleIcon,   count: counts.completed },
    { id: 'cancelled', label: 'Cancelled', icon: XCircleIcon,       count: counts.cancelled },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px' }}>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Bookings</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Review, confirm, and manage every appointment in one place
          </p>
        </div>

        {/* Banner */}
        {counts.pending > 0 ? (
          <div
            className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
            style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)' }}
          >
            <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
            <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', flex: 1, textAlign: 'left' }}>
              You have <strong>{counts.pending}</strong> booking{counts.pending === 1 ? '' : 's'} awaiting your response
            </span>
            <button
              type="button"
              onClick={() => setActiveTab('pending')}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--orange-6)',
                backgroundColor: '#fff', color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              Review now
            </button>
          </div>
        ) : (
          <div
            className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
            style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
          >
            <CheckCircleIcon style={{ width: '20px', height: '20px', color: '#22c55e' }} />
            <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
              No bookings need your attention right now
            </span>
          </div>
        )}

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
              <span style={{
                fontSize: '11px', fontWeight: 600,
                padding: '1px 7px', borderRadius: '999px',
                backgroundColor: activeTab === id ? 'var(--orange-3)' : 'var(--gray-4)',
                color: activeTab === id ? 'var(--orange-11)' : 'var(--gray-11)',
              }}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* Content card */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12"><Spinner /></div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12">
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%',
                backgroundColor: 'var(--gray-3)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
              }}>
                <CalendarDaysIcon style={{ width: '28px', height: '28px', color: 'var(--gray-9)' }} />
              </div>
              <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px' }}>
                No bookings yet
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px' }}>
                Once clients book you, their requests will show up here.
              </p>
              <Link to="/provider/profile">
                <Button>Polish your profile</Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Search */}
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <MagnifyingGlassIcon style={{
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                  width: '16px', height: '16px', color: 'var(--gray-9)', pointerEvents: 'none',
                }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by client, service, or booking ID..."
                  style={{
                    width: '100%', padding: '10px 36px 10px 38px', borderRadius: '10px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer',
                      display: 'flex',
                    }}
                  >
                    <XMarkIcon style={{ width: '16px', height: '16px', color: 'var(--gray-9)' }} />
                  </button>
                )}
              </div>

              {/* Filters row */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <select
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="all">All time</option>
                  <option value="today">Within 24 hours</option>
                  <option value="7d">Within 7 days</option>
                  <option value="30d">Within 30 days</option>
                </select>

                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="upcoming">Upcoming first</option>
                  <option value="highest">Highest earnings</option>
                  <option value="lowest">Lowest earnings</option>
                </select>

                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    style={{
                      padding: '7px 10px', borderRadius: '8px',
                      border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                      color: 'var(--gray-11)', fontSize: '13px', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <XMarkIcon style={{ width: '14px', height: '14px' }} />
                    Clear
                  </button>
                )}

                <div style={{ flex: 1 }} />

                <button
                  type="button"
                  onClick={exportCsv}
                  disabled={filtered.length === 0}
                  style={{
                    padding: '7px 10px', borderRadius: '8px',
                    border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                    color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600,
                    cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: filtered.length === 0 ? 0.5 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  <ArrowDownTrayIcon style={{ width: '14px', height: '14px' }} />
                  Export CSV
                </button>
              </div>

              {/* Result count + earnings */}
              <div className="flex items-center justify-between mb-3" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                  Showing {filtered.length === 0 ? 0 : (page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                </p>
                {filteredEarnings > 0 && (
                  <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0 }}>
                    Earnings in view: <strong style={{ color: 'var(--orange-9)' }}>{formatCurrency(filteredEarnings)}</strong>
                  </p>
                )}
              </div>

              {/* List */}
              {filtered.length === 0 ? (
                <div
                  className="text-center py-10 rounded-lg"
                  style={{ backgroundColor: 'var(--color-background)', border: '1px dashed var(--gray-5)' }}
                >
                  <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
                    No bookings match your filters.
                  </p>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      style={{
                        marginTop: '8px', padding: '6px 14px', borderRadius: '8px',
                        border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                        color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {pageItems.map((b) => {
                    const d = bookingDate(b);
                    const meta = STATUS_META[b.status] || { label: b.status, color: 'var(--gray-11)', bg: 'var(--gray-3)' };
                    const acting = actingId === b.id;
                    const earnings = earningsAmount(b);
                    const now = new Date();
                    const isPast = d && isBefore(d, now);
                    const isFuture = d && isAfter(d, now);

                    return (
                      <li
                        key={b.id}
                        className="rounded-lg"
                        style={{
                          padding: '16px',
                          backgroundColor: 'var(--color-background)',
                          border: '1px solid var(--gray-4)',
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar src={b.client?.avatarUrl} name={b.client?.name} size={44} />

                          {/* Body */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="flex items-start justify-between gap-3" style={{ flexWrap: 'wrap' }}>
                              <div>
                                <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                                  {b.service?.name || b.serviceName || 'Service'}
                                  {b.client?.name && (
                                    <span style={{ color: 'var(--gray-10)', fontWeight: 400 }}>
                                      {' '}· {b.client.name}
                                    </span>
                                  )}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1" style={{ marginTop: '4px' }}>
                                  {d && (
                                    <span className="flex items-center gap-1" style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                                      <CalendarDaysIcon style={{ width: '12px', height: '12px' }} />
                                      {format(d, 'EEE, MMM d, yyyy')}
                                    </span>
                                  )}
                                  {d && (
                                    <span className="flex items-center gap-1" style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                                      <ClockIcon style={{ width: '12px', height: '12px' }} />
                                      {format(d, 'p')}
                                    </span>
                                  )}
                                  {earnings > 0 && (
                                    <span className="flex items-center gap-1" style={{ fontSize: '12px', color: 'var(--orange-9)', fontWeight: 600 }}>
                                      <CurrencyDollarIcon style={{ width: '12px', height: '12px' }} />
                                      {formatCurrency(earnings)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span
                                style={{
                                  padding: '3px 10px', borderRadius: '999px',
                                  fontSize: '11px', fontWeight: 600,
                                  backgroundColor: meta.bg, color: meta.color,
                                  whiteSpace: 'nowrap', flexShrink: 0,
                                }}
                              >
                                {meta.label}
                              </span>
                            </div>

                            {/* Client contact + booking ID */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1" style={{ marginTop: '6px' }}>
                              {b.client?.phone && (
                                <a
                                  href={`tel:${b.client.phone}`}
                                  className="flex items-center gap-1"
                                  style={{ fontSize: '12px', color: 'var(--gray-11)', textDecoration: 'none' }}
                                >
                                  <PhoneIcon style={{ width: '12px', height: '12px' }} />
                                  {b.client.phone}
                                </a>
                              )}
                              {b.client?.email && (
                                <a
                                  href={`mailto:${b.client.email}`}
                                  className="flex items-center gap-1"
                                  style={{ fontSize: '12px', color: 'var(--gray-11)', textDecoration: 'none' }}
                                >
                                  <EnvelopeIcon style={{ width: '12px', height: '12px' }} />
                                  {b.client.email}
                                </a>
                              )}
                              {b.id && (
                                <span style={{ fontSize: '11px', color: 'var(--gray-9)' }}>
                                  #{b.id.toString().slice(-8).toUpperCase()}
                                </span>
                              )}
                            </div>

                            {/* Notes */}
                            {b.notes && (
                              <p style={{
                                fontSize: '13px', color: 'var(--gray-11)',
                                margin: '8px 0 0', padding: '8px 10px',
                                backgroundColor: 'var(--gray-2)', borderRadius: '8px',
                                textAlign: 'left',
                              }}>
                                <strong style={{ color: 'var(--gray-12)' }}>Note:</strong> {b.notes}
                              </p>
                            )}

                            {/* Action row */}
                            <div className="flex flex-wrap gap-2" style={{ marginTop: '12px' }}>
                              {b.status === 'PENDING' && (
                                <>
                                  <button
                                    onClick={() => handleAction(b, 'confirm')}
                                    disabled={acting}
                                    style={{
                                      padding: '6px 14px', borderRadius: '8px', border: 'none',
                                      backgroundColor: '#22c55e', color: '#fff',
                                      fontSize: '13px', fontWeight: 600,
                                      cursor: acting ? 'wait' : 'pointer',
                                      opacity: acting ? 0.6 : 1,
                                    }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => handleAction(b, 'decline')}
                                    disabled={acting}
                                    style={{
                                      padding: '6px 14px', borderRadius: '8px',
                                      border: '1px solid var(--red-6, #fca5a5)',
                                      backgroundColor: 'transparent', color: 'var(--red-9, #e5484d)',
                                      fontSize: '13px', fontWeight: 600,
                                      cursor: acting ? 'wait' : 'pointer',
                                      opacity: acting ? 0.6 : 1,
                                    }}
                                  >
                                    Decline
                                  </button>
                                </>
                              )}
                              {CONFIRMED_SET.has(b.status) && (
                                <>
                                  {!isFuture && (
                                    <button
                                      onClick={() => handleAction(b, 'complete')}
                                      disabled={acting}
                                      style={{
                                        padding: '6px 14px', borderRadius: '8px', border: 'none',
                                        backgroundColor: 'var(--orange-9)', color: '#fff',
                                        fontSize: '13px', fontWeight: 600,
                                        cursor: acting ? 'wait' : 'pointer',
                                        opacity: acting ? 0.6 : 1,
                                      }}
                                    >
                                      Mark Complete
                                    </button>
                                  )}
                                  {isPast && (
                                    <button
                                      onClick={() => handleAction(b, 'no-show')}
                                      disabled={acting}
                                      style={{
                                        padding: '6px 14px', borderRadius: '8px',
                                        border: '1px solid var(--gray-5)',
                                        backgroundColor: 'transparent', color: 'var(--gray-11)',
                                        fontSize: '13px', fontWeight: 600,
                                        cursor: acting ? 'wait' : 'pointer',
                                        opacity: acting ? 0.6 : 1,
                                      }}
                                    >
                                      No-show
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleAction(b, 'cancel')}
                                    disabled={acting}
                                    style={{
                                      padding: '6px 14px', borderRadius: '8px',
                                      border: '1px solid var(--red-6, #fca5a5)',
                                      backgroundColor: 'transparent', color: 'var(--red-9, #e5484d)',
                                      fontSize: '13px', fontWeight: 600,
                                      cursor: acting ? 'wait' : 'pointer',
                                      opacity: acting ? 0.6 : 1,
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                              {COMPLETED_SET.has(b.status) && (
                                <span style={{ fontSize: '12px', color: 'var(--gray-10)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <CheckCircleIcon style={{ width: '14px', height: '14px', color: '#22c55e' }} />
                                  Completed
                                </span>
                              )}
                              {CANCELLED_SET.has(b.status) && b.cancelReason && (
                                <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                                  Reason: {b.cancelReason}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Pagination */}
              {filtered.length > PER_PAGE && (
                <div className="flex items-center justify-between mt-5 pt-4" style={{ borderTop: '1px solid var(--gray-4)' }}>
                  <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                        cursor: page === 1 ? 'default' : 'pointer',
                        backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                        opacity: page === 1 ? 0.4 : 1,
                      }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) => p === '...' ? (
                        <span key={`gap-${i}`} style={{ padding: '0 6px', color: 'var(--gray-9)', fontSize: '13px', alignSelf: 'center' }}>…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          style={{
                            width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                            fontSize: '13px', fontWeight: page === p ? 600 : 400, cursor: 'pointer',
                            backgroundColor: page === p ? 'var(--orange-9)' : 'var(--gray-4)',
                            color: page === p ? '#fff' : 'var(--gray-11)',
                          }}
                        >
                          {p}
                        </button>
                      ))}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                        cursor: page === totalPages ? 'default' : 'pointer',
                        backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                        opacity: page === totalPages ? 0.4 : 1,
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderBookings;
